import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import posthog from 'posthog-js'
import { authService } from "../features/auth/service.ts";
import type { AuthUser, SignupInitResponse } from "../features/auth/types.ts";
import { authStorage } from "../lib/storage/authStorage.ts";

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  login: (identifier: string, password: string) => Promise<AuthUser>;
  signup: (name: string, phone: string, role: string, email?: string, password?: string) => Promise<SignupInitResponse>;
  loginWithSession: (session: { accessToken: string; user: AuthUser }) => void;
  refreshUser: () => Promise<AuthUser | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(authStorage.getToken());
  const [user, setUser] = useState<AuthUser | null>(authStorage.getUser());
  const [isAuthReady, setIsAuthReady] = useState(false);

  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    const storedToken = authStorage.getToken();
    if (!storedToken) {
      authStorage.clearSession();
      setToken(null);
      setUser(null);
      return null;
    }

    try {
      const currentUser = await authService.getCurrentUser();
      setToken(storedToken);
      setUser(currentUser);
      authStorage.setSession(storedToken, currentUser);
      try { posthog.identify(currentUser.id); posthog.capture('user_session_refreshed', { user_id: currentUser.id }) } catch (e) {}
      return currentUser;
    } catch {
      authStorage.clearSession();
      setToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const bootstrapAuth = async () => {
      const storedToken = authStorage.getToken();
      if (!storedToken) {
        setIsAuthReady(true);
        return;
      }

      // Login/signup already return the full profile via /api/v1/profile shape.
      // Only call refreshUser if we have a token but no stored user (e.g. storage cleared).
      const storedUser = authStorage.getUser();
      if (!storedUser) {
        try {
          await refreshUser();
        } catch {
          // ignore — refreshUser clears session on 401
        }
      }
      setIsAuthReady(true);
    };

    void bootstrapAuth();
  }, [refreshUser]);

  // The cached `user` object only otherwise updates on login/refreshUser() —
  // an already-logged-in tab can sit for hours with values that only change
  // server-side (an admin toggles a verification gate, verifies the account,
  // grants a subscription, etc.) and never learn about it, since nothing the
  // user does locally triggers a refetch. Two triggers, throttled to at most
  // once every 60s combined so rapid focus/visibility churn doesn't spam
  // /profile: tab/window regaining focus, and a plain 5-minute interval
  // while the tab stays open.
  useEffect(() => {
    if (!token) return;
    let lastRefreshAt = 0;
    const MIN_INTERVAL_MS = 60_000;
    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastRefreshAt < MIN_INTERVAL_MS) return;
      lastRefreshAt = now;
      void refreshUser();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") maybeRefresh();
    };
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = window.setInterval(maybeRefresh, 5 * 60_000);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(interval);
    };
  }, [token, refreshUser]);

  const login = async (identifier: string, password: string) => {
    const session = await authService.login(identifier, password);
    authStorage.setSession(session.accessToken, session.user);
    setToken(session.accessToken);
    setUser(session.user);
    try {
      posthog.identify(session.user.id)
      posthog.capture('user_logged_in', { user_id: session.user.id, method: 'password' })
    } catch (e) {}
    return session.user;
  };

  const signup = async (name: string, phone: string, role: string, email?: string, password?: string) => {
    // Account not created yet — OTP step follows.
    return authService.signup(name, phone, role, email, password);
  };

  const loginWithSession = (session: { accessToken: string; user: AuthUser }) => {
    authStorage.setSession(session.accessToken, session.user);
    setToken(session.accessToken);
    setUser(session.user);
    try {
      posthog.identify(session.user.id);
      posthog.capture('user_signed_up', { user_id: session.user.id, role: session.user.role });
    } catch (e) {}
  };

  const logout = () => {
    // Fire-and-forget: deactivate FCM token on backend, then clear local session
    authService.logout().catch(() => undefined);
    localStorage.clear();
    sessionStorage.clear();
    setToken(null);
    setUser(null);
    try { posthog.capture('user_logged_out') } catch (e) {}
    // Force a full reload so all in-memory component/page state (not just storage)
    // is wiped — otherwise stale data from the previous account lingers until
    // each component happens to refetch.
    window.location.href = "/login";
  };

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      isAuthReady,
      login,
      signup,
      loginWithSession,
      refreshUser,
      logout,
    }),
    [user, token, isAuthReady, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
